import React from 'react';

interface DeleteRecordConfirmationModalProps {
  isOpen: boolean;
  recordType: string;
  recordIdentifier: string;
  anomalyDescription: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteRecordConfirmationModal: React.FC<DeleteRecordConfirmationModalProps> = ({
  isOpen,
  recordType,
  recordIdentifier,
  anomalyDescription,
  onConfirm,
  onCancel
}) => {
  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .delete-record-confirm-button:hover {
          background-color: #c62828;
          transform: scale(1.02);
        }

        .delete-record-cancel-button:hover {
          background-color: #e0e0e0;
        }

        @keyframes modalFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes modalSlideIn {
          from {
            transform: scale(0.9) translateY(-20px);
            opacity: 0;
          }
          to {
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        }

        .delete-record-modal-overlay {
          animation: modalFadeIn 0.2s ease-out;
        }

        .delete-record-modal-content {
          animation: modalSlideIn 0.3s ease-out;
        }
      `}</style>
      <div style={styles.overlay} onClick={onCancel} className="delete-record-modal-overlay">
        <div style={styles.modal} onClick={e => e.stopPropagation()} className="delete-record-modal-content">
          <div style={styles.modalHeader}>
            <div style={styles.iconContainer}>
              <span style={styles.warningIcon}>⚠️</span>
            </div>
            <h2 style={styles.modalTitle}>Delete Flagged Record?</h2>
          </div>

          <div style={styles.content}>
            <p style={styles.message}>Are you sure you want to delete this flagged record?</p>

            <div style={styles.recordPreview}>
              <div style={styles.previewRow}>
                <span style={styles.previewLabel}>Record Type:</span>
                <span style={styles.previewValue}>{recordType}</span>
              </div>
              <div style={styles.previewRow}>
                <span style={styles.previewLabel}>Identifier:</span>
                <span style={styles.previewValue}>{recordIdentifier}</span>
              </div>
              <div style={styles.previewRow}>
                <span style={styles.previewLabel}>Issue:</span>
                <span style={styles.previewValue}>{anomalyDescription}</span>
              </div>
            </div>

            <p style={styles.warning}>
              This action cannot be undone. Deleting HACCP records may affect traceability compliance.
            </p>
          </div>

          <div style={styles.buttonContainer}>
            <button onClick={onCancel} style={styles.cancelButton} className="delete-record-cancel-button">
              Cancel
            </button>
            <button onClick={onConfirm} style={styles.confirmButton} className="delete-record-confirm-button">
              Delete Record
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '480px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    overflow: 'hidden'
  },
  modalHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px 24px 16px 24px',
    borderBottom: '1px solid #f0f0f0'
  },
  iconContainer: {
    marginBottom: '12px'
  },
  warningIcon: {
    fontSize: '48px',
    display: 'block'
  },
  modalTitle: {
    margin: 0,
    fontSize: '22px',
    fontWeight: '600',
    color: '#333',
    textAlign: 'center'
  },
  content: {
    padding: '24px'
  },
  message: {
    margin: '0 0 16px 0',
    fontSize: '15px',
    color: '#666',
    textAlign: 'center',
    lineHeight: '1.5'
  },
  recordPreview: {
    backgroundColor: '#FFF8E1',
    border: '1px solid #FFC107',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  previewRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  previewLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#F57F17',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  previewValue: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
    wordBreak: 'break-word'
  },
  warning: {
    margin: 0,
    fontSize: '13px',
    color: '#d32f2f',
    textAlign: 'center',
    fontWeight: '500',
    lineHeight: '1.4'
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    padding: '16px 24px 24px 24px'
  },
  cancelButton: {
    flex: 1,
    padding: '12px 24px',
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  confirmButton: {
    flex: 1,
    padding: '12px 24px',
    backgroundColor: '#d32f2f',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  }
};

export default DeleteRecordConfirmationModal;
